import { AGENT_JUDGE_NUMERICAL_MANAGER } from "@/metrics/registry"

import type { MetricRecordData } from "@/types/metrics/contracts"

import { getRequiredFieldError } from "./requiredFields"

export function getMetricSaveValidationError(
  metric: Pick<
    MetricRecordData,
    "metric_manager" | "metric_metadata" | "metric_display_name"
  >,
  promptValidationError: string | null,
): string | null {
  if (promptValidationError) {
    return promptValidationError
  }

  if (metric.metric_manager === AGENT_JUDGE_NUMERICAL_MANAGER) {
    const { min_value: minValue, max_value: maxValue } =
      metric.metric_metadata ?? {}
    if (
      typeof minValue !== "number" ||
      typeof maxValue !== "number" ||
      !Number.isFinite(minValue) ||
      !Number.isFinite(maxValue)
    ) {
      return "Minimum and maximum values are required."
    }
    if (minValue >= maxValue) {
      return "Minimum value must be less than maximum value."
    }
  }

  const prePauseWindow = metric?.metric_metadata?.pre_pause_window
  if (
    prePauseWindow !== undefined &&
    prePauseWindow !== null &&
    !Number.isInteger(prePauseWindow)
  ) {
    return "Pre-Pause Window must be a whole number."
  }

  const segmentLengthSeconds = metric?.metric_metadata?.segment_length_seconds
  if (
    segmentLengthSeconds !== undefined &&
    segmentLengthSeconds !== null &&
    segmentLengthSeconds <= 0
  ) {
    return "Segment Length must be greater than 0."
  }

  const upperRegionPercentage = metric?.metric_metadata?.upper_region_percentage
  if (
    upperRegionPercentage !== undefined &&
    upperRegionPercentage !== null &&
    (upperRegionPercentage < 0 || upperRegionPercentage > 1)
  ) {
    return "Upper Region Percentage must be between 0 and 1."
  }

  const naturalnessThreshold = metric?.metric_metadata?.naturalness_threshold
  if (
    naturalnessThreshold !== undefined &&
    naturalnessThreshold !== null &&
    (naturalnessThreshold < 0 || naturalnessThreshold > 1)
  ) {
    return "Naturalness Threshold must be between 0 and 1."
  }

  const dataPointsPerFftSegment =
    metric?.metric_metadata?.data_points_per_fft_segment
  if (
    dataPointsPerFftSegment !== undefined &&
    dataPointsPerFftSegment !== null &&
    (!Number.isInteger(dataPointsPerFftSegment) || dataPointsPerFftSegment < 2)
  ) {
    return "FFT Window Size must be a whole number of at least 2."
  }

  const minSegmentSeconds = metric?.metric_metadata?.min_segment_seconds
  if (
    minSegmentSeconds !== undefined &&
    minSegmentSeconds !== null &&
    minSegmentSeconds < 0
  ) {
    return "Min Segment Length must be 0 or greater."
  }

  // Required-field checks (empty prompts, missing categories, unset custom
  // params, etc.) live in a single module so the same rules drive both the
  // red `*` markers on field labels and the disabled Create/Save button.
  return getRequiredFieldError(metric)
}
